import { useRef, useState, type UIEvent } from 'react';
import { X } from 'lucide-react';

/**
 * Modal con los Términos y Condiciones del Plan MS2. El titular SOLO puede
 * continuar al paso de pago tras:
 *   1) hacer scroll hasta el final del texto
 *   2) tildar el checkbox "Acepto términos y condiciones"
 *
 * Diseño legal: la firma del usuario sobre los T&C se considera dada cuando
 * acepta acá; el backend audita la decisión en `legajo.titular.plan-elegido`.
 */
interface Props {
  open: boolean;
  onAceptar: () => void;
  onCancelar: () => void;
  submitting?: boolean;
}

export function TyCMs2Modal({ open, onAceptar, onCancelar, submitting }: Props) {
  const [leidoCompleto, setLeidoCompleto] = useState(false);
  const [acepta, setAcepta] = useState(false);
  // Highlights del mensaje recordatorio: se activan cuando el titular hizo
  // click en "Continuar" sin cumplir la condición correspondiente y vuelven
  // a apagarse en cuanto resuelve esa condición. Permite que el mensaje sea
  // siempre visible (neutro/gris) y solo se vuelva rojo cuando hace falta
  // llamar la atención, sin perder un click — el comportamiento anterior
  // mostraba el error solo después del click y desaparecía al avanzar.
  const [highlightLeer, setHighlightLeer] = useState(false);
  const [highlightCasilla, setHighlightCasilla] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  if (!open) return null;

  const handleContinuar = () => {
    if (submitting) return;
    if (!leidoCompleto) {
      setHighlightLeer(true);
      return;
    }
    if (!acepta) {
      setHighlightCasilla(true);
      return;
    }
    onAceptar();
  };

  // Detectar scroll-al-fondo con tolerancia de 8px (sub-pixel rounding en
  // algunos browsers / zoom devuelve scrollTop+clientHeight 1-2 px por
  // debajo de scrollHeight aunque visualmente estés en el final).
  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      setLeidoCompleto(true);
      setHighlightLeer(false);
    }
  };

  const handleAceptaChange = (checked: boolean) => {
    setAcepta(checked);
    if (checked) setHighlightCasilla(false);
  };

  // Tooltips solo cuando la acción está bloqueada — explican por qué no se
  // puede avanzar. Cuando el flow está OK no mostramos tooltip (sería ruido).
  const tooltipCheckbox = !leidoCompleto
    ? 'Deslizá hasta el final para aceptar términos y condiciones.'
    : undefined;
  let tooltipContinuar: string | undefined;
  if (!leidoCompleto) {
    tooltipContinuar = 'Deslizá hasta el final para aceptar términos y condiciones.';
  } else if (!acepta) {
    tooltipContinuar = 'Marcá la casilla "Acepto los términos y condiciones" para continuar.';
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="tyc-ms2-titulo"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 12,
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 16,
          maxWidth: 760,
          width: '100%',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px -10px rgba(0,0,0,0.35)',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--color-outline)',
          }}
        >
          <h2
            id="tyc-ms2-titulo"
            className="heading-4"
            style={{ margin: 0, color: 'var(--color-primary-darker)' }}
          >
            Términos y Condiciones — Plan MS2
          </h2>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={onCancelar}
            disabled={submitting}
            style={{
              background: 'none',
              border: 'none',
              cursor: submitting ? 'not-allowed' : 'pointer',
              color: 'var(--color-element-mid)',
              // Target táctil ~44px (icono 20 + padding 12) sin correr el
              // layout: el margin negativo compensa el padding extra.
              padding: 12,
              margin: -8,
              display: 'flex',
              borderRadius: 8,
            }}
          >
            <X size={20} />
          </button>
        </header>

        <div
          ref={bodyRef}
          onScroll={handleScroll}
          style={{
            overflowY: 'auto',
            padding: '20px 24px',
            fontSize: 13,
            lineHeight: 1.55,
            color: 'var(--color-element-high)',
            flex: '1 1 auto',
          }}
        >
          <TyCContenido />
        </div>

        <footer
          style={{
            borderTop: '1px solid var(--color-outline)',
            padding: '14px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <label
            title={tooltipCheckbox}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              cursor: leidoCompleto ? 'pointer' : 'not-allowed',
              opacity: leidoCompleto ? 1 : 0.5,
              fontSize: 13,
            }}
          >
            <input
              type="checkbox"
              checked={acepta}
              disabled={!leidoCompleto || submitting}
              onChange={(e) => handleAceptaChange(e.target.checked)}
              title={tooltipCheckbox}
              style={{
                marginTop: 3,
                accentColor: 'var(--color-primary-darker)',
                cursor: leidoCompleto ? 'pointer' : 'not-allowed',
              }}
            />
            <span>
              Acepto los términos y condiciones del Plan MS2 detallados arriba.
            </span>
          </label>

          <RecordatorioMensaje
            mostrar={!leidoCompleto}
            highlight={highlightLeer}
            texto="Deslizá hasta el final para aceptar términos y condiciones."
          />
          <RecordatorioMensaje
            mostrar={leidoCompleto && !acepta}
            highlight={highlightCasilla}
            texto={'Marcá la casilla "Acepto los términos y condiciones" para continuar.'}
          />

          <div
            style={{
              display: 'flex',
              gap: 10,
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onCancelar}
              disabled={submitting}
              style={{
                // Default `btn-ghost` es transparente + borde fino. Sobre el
                // footer blanco del modal queda casi invisible — le subimos el
                // contraste con gris medio + borde marcado + texto darker.
                backgroundColor: '#d1d5db',
                borderColor: '#9ca3af',
                color: 'var(--color-primary-darker)',
                fontWeight: 700,
              }}
            >
              <span className="btn-font-style">Cancelar</span>
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleContinuar}
              disabled={submitting}
              aria-disabled={!acepta || !leidoCompleto}
              title={tooltipContinuar}
              style={{
                // Atenuado mientras no se puede continuar, pero clickeable para
                // que el handler dispare el mensaje explicativo. Opacity más
                // alta (0.7) para que el botón siga visible aunque "no listo".
                opacity: acepta && leidoCompleto ? 1 : 0.7,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              <span className="btn-font-style">
                {submitting ? 'Procesando…' : 'Continuar'}
              </span>
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

/**
 * Mensaje recordatorio que se muestra siempre que el titular no cumplió aún
 * el requisito (leer todo o tildar la casilla). Default neutro/gris para no
 * gritarle al usuario apenas abre el modal; pasa a rojo solo cuando intentó
 * continuar sin cumplir la condición y necesita que llame la atención.
 *
 * Usa `role="status"` (no "alert") porque el mensaje siempre está presente
 * mientras la condición no se cumpla — los lectores de pantalla lo anuncian
 * con menos urgencia que un alert real.
 */
function RecordatorioMensaje({
  mostrar,
  highlight,
  texto,
}: {
  mostrar: boolean;
  highlight: boolean;
  texto: string;
}) {
  if (!mostrar) return null;
  return (
    <p
      role={highlight ? 'alert' : 'status'}
      style={{
        margin: 0,
        fontSize: 13,
        color: highlight ? 'var(--color-error-dark)' : 'var(--color-element-mid)',
        background: highlight ? 'var(--color-error-lighter-ext, #fee2e2)' : 'transparent',
        border: highlight
          ? '1px solid var(--color-error-light, #fca5a5)'
          : '1px solid var(--color-outline)',
        borderRadius: 6,
        padding: '8px 12px',
        transition: 'color 0.2s ease, background 0.2s ease, border-color 0.2s ease',
      }}
    >
      {texto}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Texto completo de los T&C. Vive como JSX inline para preservar tipografía,
// listas y tabla tarifaria. Source-of-truth: PDF "tyc con cuadro de costo.pdf"
// provisto por Legales (mayo 2026).
// ---------------------------------------------------------------------------

function TyCContenido() {
  return (
    <>
      <Seccion titulo="Introducción">
        <p>
          Para quienes deseen incorporarse como asociados al PLAN MS2, deberán aceptar los términos
          y condiciones que se detallan a continuación, los cuales van a regular la relación entre
          MEDICUS S.A. de asistencia médica y científica en adelante (MEDICUS) y el asociado.
        </p>
        <p>
          Los mismos forman parte y vienen a complementar la "Declaración Jurada de enfermedades
          Preexistentes" que el asociado suscribió al momento de solicitar la incorporación a
          MEDICUS y del cuadro de costos y beneficios del Plan respectivo.
        </p>
      </Seccion>

      <Seccion titulo="1. Ámbito de aplicación">
        <p>
          El presente reviste carácter exclusivamente individual para aquellos asociados y sus
          grupos familiares activos del plan de cobertura médico asistencial brindado por MEDICUS
          "PLAN MS 1" de la OBRA SOCIAL DE LAS FUERZAS FEDERALES DE SEGURIDAD (OSFFESEG), y que se
          encuentren vigentes durante su permanencia, los cuales opten por adherir al plan
          superador "PLAN MS 2". En todos los casos, la relación se establece de manera directa
          entre el usuario y la Entidad de Medicina Prepaga o el beneficiario y el Agente del
          Seguro de Salud.
        </p>
      </Seccion>

      <Seccion titulo="2. Objeto">
        <p>
          El presente tiene por objeto el otorgamiento de prestaciones de prevención, protección,
          tratamiento y rehabilitación de la salud humana a los usuarios o beneficiarios, mediante
          sistemas pagos de adhesión, con o sin derivación de aportes y contribuciones de los
          trabajadores en relación de dependencia, o cotizaciones de los asociados adheridos al
          Régimen Simplificado de Pequeños Contribuyentes, ya sea en efectores propios o a través
          de terceros vinculados o contratados al efecto, con los alcances y extensión de cobertura
          previstos en el plan MS2.
        </p>
      </Seccion>

      <Seccion titulo="3. Cobertura prestacional">
        <p>
          MEDICUS brinda sus prestaciones de servicios médico-asistenciales a todos sus asociados
          domiciliados o con residencia permanente o transitoria en el ámbito geográfico de la
          República Argentina. Para los asociados que por descanso, turismo, razones particulares o
          laborales decidan trasladarse a otro país deberán contar con la cobertura activa a través
          de la asistencia del viajero.
        </p>
        <p>
          No tendrán cobertura médica alguna de parte de MEDICUS aquellas personas que opten por
          domiciliarse o residir, transitoria o permanentemente, en otro país, así como tampoco
          tendrán derecho a solicitar prestaciones directas ni por reintegro que sean programadas
          —derivadas de preexistencias o no.
        </p>
        <p>
          El Sistema Privado de Medicina Asistencial de MEDICUS ha sido creado para ofrecer a sus
          asociados una atención médica de la más alta jerarquía. Con ese objeto cada asociado
          recibe una tarjeta identificatoria denominada MEDICARD, mediante la cual tiene acceso al
          calificado Cuerpo Médico que figura en la nómina preparada por MEDICUS, que se entrega al
          asociado y se actualiza periódicamente.
        </p>

        <SubSeccion titulo="3.1 Acceso a la cobertura">
          <p>
            Una vez emitida el MEDICARD, los asociados que tengan actualizadas sus obligaciones con
            MEDICUS gozarán de la cobertura prevista en el presente "PLAN MS 2". La cobertura
            abarcará las prestaciones previstas en el Programa Médico Obligatorio (PMO) y se
            regirán por lo dispuesto en el presente PLAN. Las prestaciones no previstas en el mismo
            se encuentran excluidas.
          </p>
          <p>
            Quedan excluidos de dicha cobertura los planes corporativos que hayan previsto que la
            misma sea brindada por la obra social respectiva. En tales casos, las prestaciones
            serán las previstas en el contrato celebrado con la empresa u obra social.
          </p>
          <p>
            La presentación del MEDICARD permitirá obtener atención médica en los profesionales y/o
            establecimientos adheridos, dentro de las presentes condiciones del "PLAN MS 2"
            (sistema cerrado). Para gozar de cobertura en los establecimientos adheridos las
            prestaciones médicas deberán ser ordenadas y/o practicadas por profesionales adheridos
            a la cartilla.
          </p>
          <p>
            <strong>MEDICAMENTOS:</strong> MEDICUS cubre los gastos de medicamentos en las
            siguientes circunstancias:
          </p>
          <p>
            <strong>a) Farmacias adheridas:</strong> Los asociados podrán obtener el descuento
            correspondiente para la compra de medicamentos (excepto los indicados más adelante) en
            las farmacias cuya nómina figura en la cartilla, cumpliendo con los requisitos de
            presentación de MEDICARD + acreditación de identidad y receta digital o extendida por
            un médico u odontólogo adherido (validez 30 días, hasta 3 medicamentos y 2 envases de
            cada uno por receta). MEDICUS no efectuará reintegros por este concepto.
          </p>
          <p>
            Quedan excluidos: medicamentos de venta libre, recetas magistrales, medicamentos
            destinados a patologías o prestaciones no cubiertas, no aprobados por el Ministerio de
            Salud, homeopáticos, y los que tengan cobertura especial específica en el PMO.
          </p>
          <p>
            <strong>b) En internación:</strong> MEDICUS se hará cargo de los medicamentos y
            materiales descartables utilizados cuando la internación se realice en un
            establecimiento de cartilla del PLAN MS 2 y fuera indicada o practicada por un
            profesional de cartilla.
          </p>
        </SubSeccion>

        <SubSeccion titulo="3.2 Cobertura por discapacidad">
          <p>
            La cobertura integral por DISCAPACIDAD está garantizada exclusivamente para los
            beneficiarios titulares de un Certificado Único de Discapacidad (art. 10° de la Ley
            24.901). La misma será brindada exclusivamente a través de los prestadores contratados
            por MEDICUS a tal fin. Todas las prestaciones requieren auditoría previa. Comunicarse
            con el Centro de Atención al Asociado con Discapacidad 4129-5316 (lunes a viernes 11 a
            17 h) antes del inicio del tratamiento.
          </p>
          <p>
            Los elementos e insumos entregados se realizan en comodato y deben ser devueltos al
            finalizar el tratamiento o si son reemplazados. La no devolución implica que el costo
            se sume a la factura mensual posterior a la intimación.
          </p>
        </SubSeccion>

        <SubSeccion titulo="3.3 Cartilla">
          <p>
            Los profesionales, centros, laboratorios, sanatorios y otros servicios incluidos en la
            cartilla del plan MS2 están sujetos a disponibilidad, pudiendo MEDICUS reemplazarlos,
            ampliarlos y/o darlos de baja en cualquier momento, siempre y cuando dichas
            modificaciones no impliquen una disminución del nivel o calidad de las prestaciones
            contratadas. Cambios significativos se notifican con al menos 30 días de antelación. El
            asociado que no aceptare la modificación podrá rescindir el contrato sin cargo.
          </p>
        </SubSeccion>

        <SubSeccion titulo="3.4 Disponibilidad sanatorial">
          <p>
            Ante la internación de urgencia o programada, la reserva de cama está sujeta a la
            disponibilidad de la institución al momento de la internación. En urgencia, la
            internación se brinda en la institución disponible dentro de la jurisdicción del
            domicilio del afiliado. Obtenida el alta médica cesa la cobertura por tal concepto. La
            permanencia injustificada queda a exclusivo cargo del afiliado.
          </p>
        </SubSeccion>

        <SubSeccion titulo="3.5 Plan odontológico Medicus">
          <p>
            MEDICUS brindará la cobertura odontológica prevista en el PMO y conforme la Cartilla
            Odontológica del presente Plan.
          </p>
        </SubSeccion>

        <SubSeccion titulo="3.6 Uso de la credencial">
          <p>
            La credencial es de uso personal e intransferible. Para acceder al servicio deberá
            presentarse junto al documento de identidad.
          </p>
        </SubSeccion>
      </Seccion>

      <Seccion titulo="Condiciones de ingreso">
        <SubSeccion titulo="4. Formas de incorporación – Titulares, Incorporados, Beneficiarios">
          <p>
            La posibilidad de acceso al PLAN MS 2 será exclusivamente para aquellos asociados y sus
            grupos familiares activos del PLAN MS 1 de OSFFESEG vigentes durante su permanencia.
            Podrán contratar los servicios las personas legalmente capaces y las incapaces de
            ejercicio de derecho mediante sus representantes legales.
          </p>
          <p>
            La incorporación al PLAN MS 2 será para el asociado y los integrantes de su grupo
            familiar comprendidos en el art. 14 de la Ley 26.682 y su reglamentación, y el art. 9
            de la Ley 23.660. No es posible que un solo integrante del grupo familiar se adhiera al
            PLAN MS 2 en forma individual.
          </p>
          <p>
            En caso de pérdida de su calidad de asociado al PLAN MS 1, el asociado tiene derecho a
            la continuidad con su antigüedad reconocida en MEDICUS en algún plan de similares
            características para asociados generales, si lo solicita en el plazo de 60 días desde
            el cese.
          </p>
        </SubSeccion>

        <SubSeccion titulo="4.1 Incorporación">
          <p>
            La incorporación quedará perfeccionada conforme el Art. 980 del CCyC una vez que la
            adhesión sea aceptada y confirmada por MEDICUS. No implica la incorporación a MEDICUS
            la simple entrega de la documentación. Si la adhesión se efectúa mediante sistema
            electrónico, MEDICUS solicitará una foto del DNI.
          </p>
          <p>
            MEDICUS podrá optar por validar los datos personales mediante validación biométrica.
            Por el presente el asociado presta consentimiento como titular de los datos para que
            MEDICUS confronte sus datos personales (huella dactilar y reconocimiento facial) con la
            base del RENAPER. Los datos serán tratados con la exclusiva finalidad de validar la
            identidad y verificar la vigencia del DNI.
          </p>
          <p>
            <strong>Protección de datos personales:</strong> los datos aportados forman parte de un
            banco de datos de MEDICUS, con domicilio legal en Larrea 877 piso 4 CABA, y serán
            tratados para brindar servicios a los asociados. Políticas de privacidad en{' '}
            <em>www.medicus.com.ar</em>.
          </p>
          <p>
            La incorporación será por tiempo indeterminado y operará únicamente desde la fecha que
            se indique en el MEDICARD.
          </p>
        </SubSeccion>

        <SubSeccion titulo="4.2 Doble afiliación – Prohibición">
          <p>
            Conforme al Dec. 292/95, Dec. 1608/04 y concordantes, ningún beneficiario del Sistema
            Nacional del Seguro de Salud puede estar afiliado a más de un Agente de Salud, ya sea
            como beneficiario titular o como miembro del grupo familiar primario. MEDICUS se
            reserva el derecho a rescindir la contratación del afiliado que registre 2 obras
            sociales al mismo tiempo.
          </p>
          <p>
            El recién nacido podrá ser incorporado al Sistema desde el momento de su nacimiento
            presentando, en las oficinas, su DNI y un certificado médico con el día y hora del
            nacimiento.
          </p>
        </SubSeccion>

        <SubSeccion titulo="4.3 Permanencia">
          <p>
            La permanencia mínima en el PLAN MS 2 será de 6 (seis) meses contados desde la
            incorporación. En caso de falta de pago de una cuota y transcurridos 30 días desde el
            vencimiento sin cancelar el saldo, el asociado y su grupo familiar dejarán de ser
            beneficiarios del PLAN MS 2 y pasarán automáticamente al PLAN MS 1 por un período de 12
            meses. Reincorporación posterior requiere cancelación de la deuda pendiente.
          </p>
          <p>
            Todo asociado podrá rescindir unilateralmente su incorporación, una vez transcurridos
            los 6 meses, sin expresión de causa, comunicándolo en forma fehaciente con 30 días de
            anticipación y restituyendo el MEDICARD. La rescisión podrá realizarse sólo 1 vez por
            año. Efectuada la rescisión, el asociado pasa automáticamente al PLAN MS 1; en caso de
            re-ingreso al PLAN MS 2 hay un período de espera de 12 meses contados desde la baja.
          </p>
        </SubSeccion>
      </Seccion>

      <Seccion titulo="Incumplimientos – Revocación y término de la incorporación">
        <SubSeccion titulo="5. Revocación del contrato">
          <p>
            La comprobación por MEDICUS de falsedades en la declaración, ocultación, reticencia u
            omisión de circunstancias o antecedentes relativos al asociado titular, incorporados o
            beneficiarios, facultará a MEDICUS a dejar sin efecto la incorporación al PLAN MS 2 y a
            remitir al PLAN MS 1, con pérdida de las sumas pagadas y exigibilidad de las
            adeudadas, además de exigir la restitución de las prestaciones efectuadas, siempre que
            se compruebe que el usuario no obró de buena fe (art. 961 CCyCN).
          </p>
        </SubSeccion>

        <SubSeccion titulo="5.1 Incumplimiento">
          <p>
            Constituyen incumplimiento, entre otros: omitir notificar el extravío de credencial
            generando daño a MEDICUS; facilitar credenciales a terceros; solicitar servicios para
            personas no asociadas; uso indebido o doloso del servicio; conductas violentas y/o
            amenazas al personal o prestadores. Acreditado el incumplimiento, MEDICUS podrá
            notificar, suspender la prestación y/o resolver el contrato con justa causa.
          </p>
        </SubSeccion>

        <SubSeccion titulo="5.2 Falta de pago">
          <p>
            Se producirá la resolución del contrato en caso de falta de pago de UNA (1) cuota,
            previa intimación fehaciente a regularizar la deuda en el plazo de 30 días. La falta
            de pago en término implica morosidad y MEDICUS queda facultada a limitar las
            prestaciones según el PMO, sin previa intimación, sin perjuicio de rescindir la
            afiliación. Producida la desvinculación, quedarán desactivadas las aplicaciones y
            credenciales.
          </p>
        </SubSeccion>

        <SubSeccion titulo="5.3 Muerte del titular">
          <p>
            La muerte del titular no produce per se la resolución de pleno derecho. Los
            incorporados y/o beneficiarios podrán continuar asociados a MEDICUS solicitando dentro
            de los 30 días, sin que medie espera alguna. La nueva asociación se efectiviza a
            solicitud del familiar mayor de edad y/o tutor y/o representante legal.
          </p>
        </SubSeccion>
      </Seccion>

      <Seccion titulo="6. Limitaciones y exclusiones">
        <ul style={{ paddingLeft: 20, margin: '8px 0', display: 'grid', gap: 6 }}>
          <li>Medicamentos de venta libre y recetas magistrales.</li>
          <li>
            Terapias alternativas para cualquier enfermedad incluidas las oncológicas o malignas.
            Citostáticos no aprobados o en experimentación.
          </li>
          <li>
            Métodos de diagnóstico o tratamiento no incluidos en el Programa de Garantía de
            Calidad del Ministerio de Salud, o no convencionales, en vía experimental o sin aval
            científico.
          </li>
          <li>Estudios, prácticas y/o tratamientos ambulatorios o en internación fuera del país.</li>
          <li>
            Medicamentos no fabricados ni comercializados en el país, no aprobados por ANMAT o
            indicaciones <em>off label</em>.
          </li>
          <li>Prótesis y ortesis miogénicas o bioeléctricas.</li>
          <li>Cirugías y tratamientos con fines estéticos no reparadores.</li>
          <li>
            Internaciones en centros de ancianos, geriátricos, cura de reposo, adelgazamiento o
            rejuvenecimiento; dietología, cosmetología, podología; objetos de confort personal.
          </li>
          <li>
            Servicios no prescriptos por médico; tratamientos proporcionados por familiares
            directos del afiliado salvo que pertenezcan al listado de prestadores Medicus.
          </li>
          <li>Complementos o suplementos alimenticios ambulatorios (salvo casos críticos en internación).</li>
          <li>
            Internaciones para efectuar exámenes que puedan ser ambulatorios (laboratorio,
            kinesiología, polisomnografía, etc.).
          </li>
          <li>
            Tratamientos de enfermedades crónicas e hidroterapia una vez superados los topes del
            PMO. Internaciones superados los topes del PMO.
          </li>
          <li>Transfusiones a domicilio (excepto pacientes crónicos sin desplazamiento posible).</li>
          <li>Estudios genéticos no autorizados expresamente por auditoría médica.</li>
          <li>Internaciones prolongadas por decisión del paciente/familia luego del alta médica.</li>
          <li>Tratamientos u operaciones en etapa experimental no reconocidos en Argentina.</li>
          <li>Gastos extras dentro o fuera de la internación.</li>
          <li>Material descartable de uso ambulatorio no prescripto o de venta libre.</li>
          <li>
            Prótesis u ortesis con marca/proveedor específico o importadas cuando existan
            equivalentes nacionales.
          </li>
          <li>Fertilización asistida, salvo lo contemplado en la Ley 26.862.</li>
          <li>Enfermeros, cuidadores y acompañante terapéutico para tareas no médicas.</li>
          <li>Leches maternizadas a partir de los 12 meses de vida.</li>
        </ul>
      </Seccion>

      <Seccion titulo="7. Cuotas mensuales">
        <SubSeccion titulo="7.1 Composición de la cuota">
          <p>
            Los valores se conforman en base a la cantidad de integrantes del grupo y la edad de
            los mismos, siguiendo las franjas etarias con su correspondiente incremento de acuerdo
            al cuadro tarifario adjunto, el cual se actualizará con los aumentos sucesivos:
          </p>
          <TablaTarifas />
        </SubSeccion>

        <SubSeccion titulo="7.2 Métodos de pago">
          <p>
            Las facturas deberán ser abonadas mensualmente en la fecha de vencimiento en:
            Rapipago, Pago Fácil, Pago Mis Cuentas, Red Link, Bapro Pago, adhesión a débito
            automático en cuenta o tarjeta de crédito, depósito bancario, pago on-line con tarjeta,
            y/o los medios que MEDICUS designe. La falta de recepción de la factura no exime al
            asociado de la obligación de pagar en término.
          </p>
          <p>
            <strong>Deudor moroso:</strong> deberá abonar las facturas adeudadas más interés
            punitorio según la tasa libre anual vencida activa del Banco Nación, además de gastos
            y costos administrativos, judiciales y extrajudiciales.
          </p>
        </SubSeccion>

        <SubSeccion titulo="7.3 Actualización de precios">
          <p>
            MEDICUS comunicará toda modificación en el valor de las cuotas y/o copagos, indicando
            el porcentaje aplicado y el nuevo valor, dentro de los 5 días posteriores a la
            publicación del IPC del INDEC. Los aumentos serán notificados con una antelación de 30
            días corridos previos al vencimiento de la obligación de pago.
          </p>
        </SubSeccion>
      </Seccion>

      <Seccion titulo="Subrogación">
        <p>
          MEDICUS tendrá derecho a subrogarse en los derechos de un asociado de conformidad con la
          legislación vigente, respecto de la totalidad de gastos, honorarios, etc., en
          situaciones donde el asociado tenga derecho a ser resarcido por terceros responsables,
          aseguradoras u otros. El asociado deberá cooperar íntegramente con MEDICUS en sus
          esfuerzos por obtener el pago o reembolso.
        </p>
      </Seccion>

      <Seccion titulo="Consentimiento para acceso a registros médicos">
        <p>
          Con el objetivo de recibir la mejor asistencia posible, concedo autorización para que a
          solicitud de MEDICUS se le permita acceder a los datos personales vinculados a mi salud
          que son producto de atenciones con diferentes profesionales, hospitales, sanatorios o
          instituciones. Mi historial puede incluir: diagnósticos, prescripciones e indicaciones
          médicas, resultados de laboratorio e informes de prácticas. Expreso este consentimiento
          de forma voluntaria con el único objetivo de favorecer el ejercicio de la medicina de
          forma efectiva y personalizada y facilitar la toma de decisiones médicas informadas en
          relación a mis tratamientos.
        </p>
      </Seccion>

      <Seccion titulo="Cobertura — Plan MS 2">
        <TablaCobertura />
      </Seccion>
    </>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 18 }}>
      <h3
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: 'var(--color-primary-darker)',
          margin: '0 0 8px',
        }}
      >
        {titulo}
      </h3>
      <div style={{ display: 'grid', gap: 8 }}>{children}</div>
    </section>
  );
}

function SubSeccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 10 }}>
      <h4
        style={{
          fontSize: 13.5,
          fontWeight: 700,
          color: 'var(--color-element-high)',
          margin: '0 0 6px',
        }}
      >
        {titulo}
      </h4>
      <div style={{ display: 'grid', gap: 6 }}>{children}</div>
    </div>
  );
}

function TablaTarifas() {
  const filas = [
    { etiqueta: 'Cuota por cápita', menor45: '199.700', medio: '315.000', mayor60: '517.000' },
  ];
  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 12.5,
          marginTop: 6,
          border: '1px solid var(--color-outline)',
        }}
      >
        <thead>
          <tr style={{ background: 'var(--color-surface-accent, #f3f6fb)' }}>
            <th colSpan={4} style={tdHeader}>
              VALORES SIN IVA — junio 2026 (en pesos)
            </th>
          </tr>
          <tr>
            <th style={tdHeader}></th>
            <th style={tdHeader}>Menor de 45</th>
            <th style={tdHeader}>De 45 a 59</th>
            <th style={tdHeader}>60 y más</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr key={f.etiqueta}>
              <td style={td}>{f.etiqueta}</td>
              <td style={tdNum}>{f.menor45}</td>
              <td style={tdNum}>{f.medio}</td>
              <td style={tdNum}>{f.mayor60}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const td: React.CSSProperties = {
  padding: '8px 10px',
  borderTop: '1px solid var(--color-outline)',
};
const tdNum: React.CSSProperties = { ...td, textAlign: 'right' };
const tdHeader: React.CSSProperties = {
  padding: '8px 10px',
  textAlign: 'left',
  fontWeight: 700,
  borderBottom: '1px solid var(--color-outline)',
};

function TablaCobertura() {
  const grupos: Array<{ titulo: string; items: Array<[string, string]> }> = [
    {
      titulo: 'Módulo de cobertura primaria y ambulatoria — Consultas',
      items: [['En guardia, consultorio, sanatorio o virtuales', '100% sin límite']],
    },
    {
      titulo: 'Urgencias y emergencias',
      items: [
        ['Atención de emergencia (riesgo de vida)', '100% sin límite'],
        ['Atención virtual de guardia', '100% sin límite'],
        ['Atención domiciliaria (viático $20.000)', 'Con arancel — sin límite'],
        ['Traslados programados (con autorización)', '100%'],
      ],
    },
    {
      titulo: 'Medicamentos ambulatorios',
      items: [
        ['En farmacias adheridas', '40% de descuento'],
        ['Anticonceptivos', '100%'],
      ],
    },
    {
      titulo: 'Prácticas y estudios de diagnóstico',
      items: [
        ['Análisis clínicos', '100% sin límite'],
        ['Radiografía, ecografía, TC, RM', '100% sin límite'],
        ['Tratamiento fleboesclerosante (12 sesiones/integrante/año)', '100% con límite'],
        ['Kinesiología (25 sesiones/integrante/año, viático $20.000)', '100% con límite'],
        ['Fonoaudiología (25 sesiones/integrante/año)', '100% con límite'],
      ],
    },
    {
      titulo: 'Salud mental',
      items: [
        ['Consultas psicológicas (arancel adicional $10.000)', 'Con arancel — sin límite'],
        ['Internación psiquiátrica (30 días)', '100% con límite'],
      ],
    },
    {
      titulo: 'Internación y cirugías',
      items: [
        ['Terapia intensiva / Unidad coronaria', '100% sin límite'],
        ['Pensión sanatorial (habitación individual)', '100% sin límite'],
        ['Honorarios médicos', '100% sin límite'],
        ['Derechos operatorios', '100% sin límite'],
        ['Medicamentos y materiales descartables', '100% sin límite'],
        ['Servicio de acompañante (3 días sin cargo)', '—'],
        ['Prótesis nacionales', '100%'],
        ['Ortesis nacionales', '50%'],
      ],
    },
    {
      titulo: 'Materno-infantil',
      items: [
        ['Control del embarazo', '100% sin límite'],
        ['Estudios específicos', '100% sin límite'],
        ['Curso preparto', '100%'],
        ['Parto / cesárea (internación, honorarios, medicamentos)', '100% sin límite'],
        ['Consultas, prácticas y medicamentos hasta el 1° año', '100% sin límite'],
        ['Vacunas calendario nacional hasta el 1° año', '100%'],
      ],
    },
    {
      titulo: 'Odontología',
      items: [
        ['Guardia, consultas, obturación, conducto, limpieza, extracciones', '100% sin límite'],
        ['Radiología odontológica', '100% sin límite'],
      ],
    },
    {
      titulo: 'Beneficios exclusivos MS 2',
      items: [
        ['Centros Medicus — atención exclusiva', '100%'],
        ['Cirugía oftalmológica Eximia láser (con autorización previa)', '100%'],
        ['Vacunas calendario nacional', '100%'],
        ['Vacunas no incluidas en calendario nacional', '40% de descuento'],
        [
          'Seguro del viajero al exterior (30 días por viaje, hasta 30.000 EUR/USD)',
          'Incluido',
        ],
      ],
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {grupos.map((g) => (
        <div
          key={g.titulo}
          style={{
            border: '1px solid var(--color-outline)',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '6px 10px',
              background: 'var(--color-surface-accent, #f3f6fb)',
              fontWeight: 700,
              fontSize: 12.5,
            }}
          >
            {g.titulo}
          </div>
          {/* Scroll horizontal en pantallas angostas: la col de cobertura es
              `nowrap` y en ~320px el ancho mínimo supera el del modal. Mismo
              recurso que `TablaTarifas` para no pellizcar el layout. */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <tbody>
                {g.items.map(([prestacion, cobertura]) => (
                  <tr key={prestacion}>
                    <td style={td}>{prestacion}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {cobertura}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <p style={{ fontSize: 11.5, color: 'var(--color-element-mid)', margin: 0 }}>
        Cirugía oftalmológica requiere autorización previa de auditoría médica y un tiempo de
        espera inicial de 12 meses para estudios de alta complejidad. Astigmatismo: mayor de 25
        años y dioptrías {'>'} 2,5. Miopía: mayor de 25 años y dioptrías {'>'} 4,5. Hipermetropía:
        mayor de 50 años y dioptrías {'>'} 3.
      </p>
    </div>
  );
}
